import { Announcement } from '@servicetitan/design-system';
import useAnnouncements from '../hooks/useAnnouncements';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import useService from '@modules/ServiceModule/useService';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { useFeatureFlags, FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { strings } from '../strings';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { phoneState } from '@modules/SoftPhoneModule/state';
import { hasValidSeat } from 'src/constants/user';
import {
  LegacyJwtFetchStatus,
  WebphoneConnectionStatus,
} from '@modules/SoftPhoneModule/types';

/**
 * Responsible for reading the announcements from the AnnouncementsModule and rendering them.
 */
const Announcements = () => {
  const { announcements } = useAnnouncements();
  const {
    phoneConnected,
    registrationOverwritten,
    disconnectTriggeredLocally,
    existingRegistrationsDetected,
    isUnactivatedImpersonator,
    connectionStatus,
    legacyJwtFetchStatus,
  } = usePhoneState();
  const { serviceConnected } = useService();
  const { user } = useAuth();
  const {
    [FeatureFlagKeys.featurePreventMultipleRegistrations]:
      preventMultipleRegistrationsEnabled,
    [FeatureFlagKeys.featureRtcWebphoneHandlesFetchingJwt]:
      rtcWebphoneHandlesFetchingJwt,
  } = useFeatureFlags();

  const handleActivateWebPhone = () => {
    SoftPhoneModule.activateWebPhone();
  };

  const handleImpersonationActivatePhone = () => {
    phoneState.isUnactivatedImpersonator = false;
  };

  const hasActiveRegistrationElsewhere =
    existingRegistrationsDetected || registrationOverwritten;

  const hasSeat = hasValidSeat(user?.seatLevel);

  const hasConnectionError =
    connectionStatus === WebphoneConnectionStatus.RegistrationFailed ||
    connectionStatus === WebphoneConnectionStatus.WebphoneJwtFetchFailed ||
    connectionStatus === WebphoneConnectionStatus.WebphoneJwtNotAvailable;

  return (
    <>
      {/*preventMultipleRegistrationsEnabled ON */}
      {preventMultipleRegistrationsEnabled &&
        !phoneConnected &&
        hasActiveRegistrationElsewhere && (
          <Announcement
            status='warning'
            title={strings.noActiveWebphone}
            actionName={strings.noActiveWebphoneAction}
            onActionClick={handleActivateWebPhone}
          />
        )}
      {/* preventMultipleRegistrationsEnabled OFF */}
      {!preventMultipleRegistrationsEnabled &&
        !phoneConnected &&
        registrationOverwritten && (
          <Announcement
            status='warning'
            title={strings.registrationOverwritten}
          />
        )}
      {/* Connection error announcement */}
      {hasSeat &&
        !phoneConnected &&
        hasConnectionError &&
        !hasActiveRegistrationElsewhere && (
          <Announcement
            status='critical'
            title={strings.webPhoneConnectionError}
            actionName={strings.webPhoneConnectAction}
            onActionClick={handleActivateWebPhone}
          />
        )}
      {/* Legacy JWT fetch failure — only when host app is responsible for JWT fetching */}
      {hasSeat &&
        !rtcWebphoneHandlesFetchingJwt &&
        legacyJwtFetchStatus === LegacyJwtFetchStatus.Failed && (
          <Announcement
            status='critical'
            title={strings.webPhoneConnectionError}
            actionName={strings.webPhoneConnectAction}
            onActionClick={() => SoftPhoneModule.retryLegacyJwtFetch()}
          />
        )}
      {/* we want to show the below banner if the softphone supposed to be connected, but it is not, we also don't want to show it when multiple tabs are open  */}
      {hasSeat &&
        !phoneConnected &&
        serviceConnected &&
        !hasActiveRegistrationElsewhere &&
        !disconnectTriggeredLocally &&
        !hasConnectionError &&
        (user?.isImpersonated && isUnactivatedImpersonator ? (
          <Announcement
            status='warning'
            title={strings.webPhoneImpersonating}
            actionName={strings.noActiveWebphoneAction}
            onActionClick={handleImpersonationActivatePhone}
          />
        ) : (
          <Announcement status='warning' title={strings.webPhoneReconnecting} />
        ))}
      {Object.entries(announcements).map(([key, announcement]) => (
        <Announcement {...announcement} key={key} />
      ))}
    </>
  );
};

export default Announcements;
